import { useState } from "react";
import { rawFileUrl } from "../lib/api";
import ExecutionPanel from "./ExecutionPanel";

/**
 * Collapsible tool-call chip.
 *
 * The reviewer sees a small badge in the chat with the tool name. Clicking it
 * expands to show the JSON args and the result preview (first ~400 chars).
 *
 * If the tool returned an `artifact_path` (e.g. a plot PNG), the artifact is
 * rendered inline below the result.
 *
 * Special case: `run_on_aws` returns an `executionId`; we render the
 * ExecutionPanel inline so the reviewer can Approve / Reject right in the
 * chat transcript.
 */
export default function ToolChip(props: {
  name: string;
  input?: Record<string, unknown>;
  resultPreview?: string;
  artifactPath?: string | null;
  executionId?: number | null;
  isError?: boolean;
  sessionId: number;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { name, input, resultPreview, artifactPath, executionId, isError, sessionId } = props;
  const summary = oneLineArgs(input);

  // run_on_aws gets its own panel embedded inline so the reviewer can
  // Approve/Reject without expanding the chip.
  const isRunOnAws = name === "run_on_aws" && executionId !== null && executionId !== undefined;

  return (
    <div
      className={`my-1.5 rounded border text-xs ${
        isError ? "border-red-200 bg-red-50" : "border-ink-200 bg-white"
      }`}
    >
      {isRunOnAws && (
        <ExecutionPanel sessionId={sessionId} executionId={executionId as number} />
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-ink-700 hover:bg-ink-50"
      >
        <span>
          <span className="font-mono text-[11px] text-ink-500">{open ? "▾" : "▸"}</span>{" "}
          <code className="font-mono">{name}</code>
          {summary && <span className="ml-1 text-ink-500">({summary})</span>}
        </span>
        <span className="text-ink-500">
          {isError ? "error" : resultPreview ? "done" : "running…"}
        </span>
      </button>
      {open && (
        <div className="border-t border-ink-200 p-2">
          {input !== undefined && (
            <div className="mb-2">
              <div className="mb-0.5 text-[10px] uppercase tracking-wide text-ink-500">
                Args
              </div>
              <pre className="overflow-auto whitespace-pre-wrap text-[11px] text-ink-700">
                {JSON.stringify(input, null, 2)}
              </pre>
            </div>
          )}
          {resultPreview && (
            <div>
              <div className="mb-0.5 text-[10px] uppercase tracking-wide text-ink-500">
                Result preview
              </div>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-[11px] text-ink-700">
                {resultPreview}
              </pre>
            </div>
          )}
          {artifactPath && (
            <div className="mt-2">
              <div className="mb-0.5 text-[10px] uppercase tracking-wide text-ink-500">
                Artifact
              </div>
              <img
                src={rawFileUrl(sessionId, artifactPath)}
                alt={`Tool artifact ${artifactPath}`}
                className="max-h-80 max-w-full rounded border border-ink-200"
              />
            </div>
          )}
          {props.compact && !resultPreview && !artifactPath && (
            <div className="text-[11px] text-ink-500">
              (result not stored client-side — re-ask to refetch)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function oneLineArgs(input?: Record<string, unknown>): string {
  if (!input) return "";
  const entries = Object.entries(input);
  if (entries.length === 0) return "";
  const summary = entries
    .slice(0, 2)
    .map(([k, v]) => `${k}=${shortValue(v)}`)
    .join(", ");
  return entries.length > 2 ? summary + ", …" : summary;
}

function shortValue(v: unknown): string {
  if (typeof v === "string") return v.length > 20 ? `"${v.slice(0, 17)}…"` : `"${v}"`;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "…";
}
